import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaUser, FaLock, FaChevronDown, FaShieldAlt, FaEye, FaEyeSlash, FaMobileAlt, FaKey } from 'react-icons/fa';
import Toast from '../components/Toast';
import ForgotPasswordModal from '../components/ForgotPasswordModal';
import * as authService from '../services/authService';
import * as profileService from '../services/profileService';

const RESEND_COOLDOWN = 30; // seconds

const AdminUserLogin = () => {
    const navigate = useNavigate();

    /* ─── Shared state ─── */
    const [loginMode, setLoginMode] = useState('password'); // 'password' | 'otp'
    const [roles, setRoles] = useState([]);
    const [loadingRoles, setLoadingRoles] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [toast, setToast] = useState(null);
    const [showForgotPassword, setShowForgotPassword] = useState(false);

    /* ─── Password-login state ─── */
    const [formData, setFormData] = useState({
        role_id: '',
        username: '',
        password: '',
        remember_me: true,
    });
    const [showPassword, setShowPassword] = useState(false);

    /* ─── OTP-login state ─── */
    const [otpFormData, setOtpFormData] = useState({
        role_id: '',
        phone_number: '',
        remember_me: true,
    });
    const [otp, setOtp] = useState('');
    const [otpSent, setOtpSent] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);
    const timerRef = useRef(null);

    /* ─── Load roles ─── */
    useEffect(() => {
        const loadRoles = async () => {
            try {
                setLoadingRoles(true);
                const rolesData = await authService.fetchLoginRoles();
                const filteredRoles = rolesData.filter(role =>
                    role.code === 'admin' || role.code === 'user'
                );
                setRoles(filteredRoles);
            } catch (error) {
                console.error('Error fetching roles:', error);
                showToast('Failed to load roles. Please refresh the page.', 'error');
                setRoles([
                    { id: 'admin', description: 'Admin', code: 'admin' },
                    { id: 'user', description: 'User', code: 'user' },
                ]);
            } finally {
                setLoadingRoles(false);
            }
        };
        loadRoles();
    }, []);

    /* ─── Cleanup timer on unmount ─── */
    useEffect(() => () => clearInterval(timerRef.current), []);

    /* ─── Helpers ─── */
    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3500);
    };

    const startResendTimer = () => {
        setResendCooldown(RESEND_COOLDOWN);
        timerRef.current = setInterval(() => {
            setResendCooldown(prev => {
                if (prev <= 1) {
                    clearInterval(timerRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    /* ─── Session finalizer (shared) ─── */
    const finalizeLogin = async (response, roleId, rememberMe) => {
        const apiData = response.data || {};
        const userObj = apiData.user || apiData;

        const userData = {
            ...userObj,
            id: userObj.id || userObj.user_id,
            role: userObj.role_code || userObj.role || (roles.find(r => r.id === roleId)?.code) || 'user',
            is_reviewer: !!userObj.is_reviewer || !!userObj.permissions?.is_reviewer,
            is_editorial_board: !!userObj.is_editorial_board || !!userObj.permissions?.is_editorial_board,
            full_name: userObj.full_name || userObj.name || userObj.username,
            name: userObj.name || userObj.full_name,
            loginTime: new Date().toISOString(),
        };

        authService.storeSession(userData, null, null, rememberMe);

        if (!userData.full_name && !userData.name) {
            try {
                const profileResponse = await profileService.getProfile();
                if (profileResponse.success && profileResponse.data) {
                    const pData = profileResponse.data;
                    const personal = pData.personal || {};
                    const fullName = pData.full_name || personal.full_name || pData.name || personal.name ||
                        `${personal.first_name || ''} ${personal.last_name || ''}`.trim();
                    if (fullName) {
                        userData.full_name = fullName;
                        userData.name = fullName;
                        authService.storeSession(userData, null, null, rememberMe);
                    }
                }
            } catch (profileError) {
                console.error('Error fetching profile after login:', profileError);
            }
        }

        window.dispatchEvent(new Event('userLogin'));
        const userName = userData.full_name || userData.name || userData.email || 'User';
        showToast(`Welcome back ${userName}! Login successful.`, 'success');
        setTimeout(() => navigate('/'), 1000);
    };

    /* ─── Password login handlers ─── */
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        let finalValue = type === 'checkbox' ? checked : value;
        if (name === 'username' && type !== 'checkbox') {
            if (/^\d+$/.test(finalValue) && finalValue.length > 10) {
                finalValue = finalValue.slice(0, 10);
            }
        }
        setFormData({ ...formData, [name]: finalValue });
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const loginData = {
                role_id: formData.role_id,
                username: formData.username,
                password: formData.password,
                remember_me: formData.remember_me,
            };
            const response = await authService.loginUser(loginData);
            if (response.success) {
                await finalizeLogin(response, formData.role_id, formData.remember_me);
            } else {
                showToast(response.message || 'Login failed. Please try again.', 'error');
            }
        } catch (error) {
            let errorMessage = 'An error occurred during login.';
            if (error.response) {
                errorMessage = error.response.data?.message || `Error: ${error.response.status}`;
            } else if (error.request) {
                errorMessage = 'Unable to connect to the server. Please check your connection.';
            } else {
                errorMessage = error.message || 'Login failed. Please try again.';
            }
            showToast(errorMessage, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    /* ─── OTP login handlers ─── */
    const handleOtpFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        let finalValue = type === 'checkbox' ? checked : value;
        if (name === 'phone_number') {
            finalValue = finalValue.replace(/\D/g, '').slice(0, 10);
        }
        setOtpFormData({ ...otpFormData, [name]: finalValue });
    };

    const handleSendOtp = async () => {
        if (!otpFormData.role_id) {
            showToast('Please select your role first.', 'error');
            return;
        }
        if (otpFormData.phone_number.length !== 10) {
            showToast('Please enter a valid 10-digit phone number.', 'error');
            return;
        }
        setIsLoading(true);
        try {
            const response = await authService.sendLoginOtp(otpFormData.phone_number, otpFormData.role_id);
            if (response.success) {
                setOtpSent(true);
                setOtp('');
                startResendTimer();
                showToast('OTP sent to your registered phone number!', 'success');
            } else {
                showToast(response.message || 'Failed to send OTP. Please try again.', 'error');
            }
        } catch (error) {
            const msg = error?.message || error?.error || 'Failed to send OTP. Please check your phone number.';
            showToast(msg, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleOtpSubmit = async (e) => {
        e.preventDefault();
        if (otp.length !== 6) {
            showToast('Please enter the 6-digit OTP.', 'error');
            return;
        }
        setIsLoading(true);
        try {
            const response = await authService.verifyLoginOtp(
                otpFormData.phone_number,
                otpFormData.role_id,
                otp,
                otpFormData.remember_me
            );
            if (response.success) {
                await finalizeLogin(response, otpFormData.role_id, otpFormData.remember_me);
            } else {
                showToast(response.message || 'OTP verification failed.', 'error');
            }
        } catch (error) {
            const msg = error?.message || error?.error || 'Invalid or expired OTP. Please try again.';
            showToast(msg, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    /* ─── Mode switcher ─── */
    const switchMode = (mode) => {
        setLoginMode(mode);
        setOtpSent(false);
        setOtp('');
        setResendCooldown(0);
        clearInterval(timerRef.current);
    };

    /* ─── Role dropdown (shared) ─── */
    const RoleSelect = ({ value, name, onChange }) => (
        <div>
            <label htmlFor={`${name}_role_id`} className="block text-sm font-medium text-gray-700 mb-2">
                Select Role *
            </label>
            <div className="relative">
                <select
                    id={`${name}_role_id`}
                    name="role_id"
                    value={value}
                    onChange={onChange}
                    required
                    disabled={isLoading || loadingRoles}
                    className="w-full px-4 py-3 pr-10 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-purple-600 appearance-none bg-white transition-all disabled:opacity-50"
                >
                    <option value="">
                        {loadingRoles ? 'Loading roles...' : 'Choose your role'}
                    </option>
                    {roles.map((role) => (
                        <option key={role.id} value={role.id}>
                            {role.description || role.label || role.code}
                        </option>
                    ))}
                </select>
                <FaChevronDown className="absolute right-4 top-4 text-gray-400 pointer-events-none" />
            </div>
            <p className="mt-2 text-xs text-gray-500">
                <span className="font-medium text-purple-600">Note:</span>{' '}
                Select your appropriate role to access the system.
            </p>
        </div>
    );

    return (
        <>
            <ForgotPasswordModal
                isOpen={showForgotPassword}
                onClose={() => setShowForgotPassword(false)}
            />

            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}

            <div className="min-h-screen flex">
                {/* ── Left decorative panel ── */}
                <div className="hidden lg:block lg:w-1/2 fixed left-0 top-0 h-screen">
                    <div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 via-indigo-900/90 to-slate-900/90 z-10"></div>
                    <img
                        src="https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800&q=80"
                        alt="Login"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 z-20 flex flex-col items-center justify-center text-white p-12">
                        <FaShieldAlt className="text-6xl mb-6" />
                        <h2 className="text-4xl font-bold mb-4 text-center">Welcome Back!</h2>
                        <p className="text-xl text-center max-w-md">
                            Access your administrative or user dashboard securely.
                        </p>
                        <div className="mt-8 grid grid-cols-2 gap-4 text-center">
                            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                                <div className="text-3xl font-bold">Secure</div>
                                <div className="text-sm">Access</div>
                            </div>
                            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                                <div className="text-3xl font-bold">24/7</div>
                                <div className="text-sm">Available</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Right form panel ── */}
                <div className="w-full lg:w-1/2 lg:ml-[50%] min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-white p-8">
                    <div className="max-w-3xl w-full">
                        <div className="lg:hidden text-center mb-8">
                            <FaShieldAlt className="text-5xl mx-auto mb-4 text-purple-600" />
                        </div>

                        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-10">
                            <div className="text-center mb-6">
                                <h2 className="text-3xl font-bold text-gray-900">Employee Login</h2>
                                <p className="text-gray-600 mt-2">Enter your credentials to continue</p>
                            </div>

                            {/* ── Mode toggle ── */}
                            <div className="flex rounded-xl overflow-hidden border-2 border-purple-100 mb-8">
                                <button
                                    type="button"
                                    onClick={() => switchMode('password')}
                                    className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold transition-all ${loginMode === 'password'
                                        ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-md'
                                        : 'bg-white text-gray-500 hover:bg-purple-50'
                                        }`}
                                >
                                    <FaLock className="text-xs" />
                                    Login with Password
                                </button>
                                <button
                                    type="button"
                                    onClick={() => switchMode('otp')}
                                    className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold transition-all ${loginMode === 'otp'
                                        ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-md'
                                        : 'bg-white text-gray-500 hover:bg-purple-50'
                                        }`}
                                >
                                    <FaMobileAlt className="text-xs" />
                                    Login with OTP
                                </button>
                            </div>

                            {/* ════════════════════════════════
                                 PASSWORD LOGIN FORM
                                ════════════════════════════════ */}
                            {loginMode === 'password' && (
                                <form onSubmit={handlePasswordSubmit} className="space-y-6">
                                    <RoleSelect
                                        name="pw"
                                        value={formData.role_id}
                                        onChange={handleChange}
                                    />

                                    <div>
                                        <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                                            Username *
                                        </label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <FaUser className="text-gray-400" />
                                            </div>
                                            <input
                                                type="text"
                                                id="username"
                                                name="username"
                                                value={formData.username}
                                                onChange={handleChange}
                                                required
                                                disabled={isLoading}
                                                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-purple-600 transition-all disabled:opacity-50"
                                                placeholder="Enter your email or phone number"
                                            />
                                        </div>
                                        <p className="mt-1 text-xs text-purple-600 font-medium">
                                            💡 Use your registered email address or phone number
                                        </p>
                                    </div>

                                    <div>
                                        <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                                            Password *
                                        </label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <FaLock className="text-gray-400" />
                                            </div>
                                            <input
                                                type={showPassword ? 'text' : 'password'}
                                                id="password"
                                                name="password"
                                                value={formData.password}
                                                onChange={handleChange}
                                                required
                                                disabled={isLoading}
                                                className="w-full pl-10 pr-12 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-purple-600 transition-all disabled:opacity-50"
                                                placeholder="Enter your password"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-purple-600 transition-colors"
                                            >
                                                {showPassword ? <FaEyeSlash /> : <FaEye />}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center">
                                            <input
                                                id="remember_me"
                                                name="remember_me"
                                                type="checkbox"
                                                checked={formData.remember_me}
                                                onChange={handleChange}
                                                disabled={isLoading}
                                                className="h-4 w-4 text-purple-600 focus:ring-purple-600 border-gray-300 rounded cursor-pointer disabled:opacity-50"
                                            />
                                            <label htmlFor="remember_me" className="ml-2 block text-sm text-gray-700 cursor-pointer">
                                                Remember me
                                            </label>
                                        </div>
                                        <div className="text-sm">
                                            <button
                                                type="button"
                                                onClick={() => setShowForgotPassword(true)}
                                                className="text-purple-600 hover:text-indigo-600 font-medium transition-colors"
                                            >
                                                Forgot password?
                                            </button>
                                        </div>
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={isLoading}
                                        className="w-full py-3 px-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-lg hover:shadow-lg transform hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center"
                                    >
                                        {isLoading ? (
                                            <>
                                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Logging in...
                                            </>
                                        ) : (
                                            'Login to Account'
                                        )}
                                    </button>
                                </form>
                            )}

                            {/* ════════════════════════════════
                                 OTP LOGIN FORM
                                ════════════════════════════════ */}
                            {loginMode === 'otp' && (
                                <form onSubmit={handleOtpSubmit} className="space-y-6">
                                    <RoleSelect
                                        name="otp"
                                        value={otpFormData.role_id}
                                        onChange={handleOtpFormChange}
                                    />

                                    {/* Phone number + Send OTP button */}
                                    <div>
                                        <label htmlFor="phone_number" className="block text-sm font-medium text-gray-700 mb-2">
                                            Phone Number *
                                        </label>
                                        <div className="flex gap-2">
                                            <div className="relative flex-1">
                                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                    <FaMobileAlt className="text-gray-400" />
                                                </div>
                                                <input
                                                    type="tel"
                                                    id="phone_number"
                                                    name="phone_number"
                                                    value={otpFormData.phone_number}
                                                    onChange={handleOtpFormChange}
                                                    required
                                                    disabled={isLoading || otpSent}
                                                    maxLength={10}
                                                    pattern="\d{10}"
                                                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-purple-600 transition-all disabled:opacity-50"
                                                    placeholder="10-digit phone number"
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                onClick={handleSendOtp}
                                                disabled={isLoading || resendCooldown > 0}
                                                className="px-4 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-sm font-semibold rounded-lg hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                            >
                                                {isLoading && !otpSent
                                                    ? 'Sending…'
                                                    : resendCooldown > 0
                                                        ? `Resend (${resendCooldown}s)`
                                                        : otpSent
                                                            ? 'Resend OTP'
                                                            : 'Send OTP'}
                                            </button>
                                        </div>
                                        <p className="mt-1 text-xs text-purple-600 font-medium">
                                            💡 OTP will be sent to your registered phone number
                                        </p>
                                    </div>

                                    {/* OTP input — shown only after OTP is sent */}
                                    {otpSent && (
                                        <div>
                                            <label htmlFor="otp_code" className="block text-sm font-medium text-gray-700 mb-2">
                                                Enter OTP *
                                            </label>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                    <FaKey className="text-gray-400" />
                                                </div>
                                                <input
                                                    type="text"
                                                    id="otp_code"
                                                    value={otp}
                                                    onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                                    required
                                                    disabled={isLoading}
                                                    maxLength={6}
                                                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-purple-600 transition-all disabled:opacity-50 tracking-[0.4em] text-lg font-bold text-center"
                                                    placeholder="• • • • • •"
                                                    autoFocus
                                                />
                                            </div>
                                            <p className="mt-1 text-xs text-gray-500">
                                                OTP is valid for 10 minutes.
                                            </p>
                                        </div>
                                    )}

                                    {/* Remember me */}
                                    <div className="flex items-center">
                                        <input
                                            id="otp_remember_me"
                                            name="remember_me"
                                            type="checkbox"
                                            checked={otpFormData.remember_me}
                                            onChange={handleOtpFormChange}
                                            disabled={isLoading}
                                            className="h-4 w-4 text-purple-600 focus:ring-purple-600 border-gray-300 rounded cursor-pointer disabled:opacity-50"
                                        />
                                        <label htmlFor="otp_remember_me" className="ml-2 block text-sm text-gray-700 cursor-pointer">
                                            Remember me
                                        </label>
                                    </div>

                                    {/* Submit — disabled until OTP has been sent */}
                                    <button
                                        type="submit"
                                        disabled={isLoading || !otpSent}
                                        className="w-full py-3 px-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-lg hover:shadow-lg transform hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center"
                                    >
                                        {isLoading && otpSent ? (
                                            <>
                                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Verifying…
                                            </>
                                        ) : (
                                            'Login with OTP'
                                        )}
                                    </button>
                                </form>
                            )}

                            {/* ── Divider + Create Account ── */}
                            <div className="mt-6">
                                <div className="relative">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className="w-full border-t border-gray-300"></div>
                                    </div>
                                    <div className="relative flex justify-center text-sm">
                                        <span className="px-2 bg-white text-gray-500">Or</span>
                                    </div>
                                </div>
                                <div className="text-center mt-4">
                                    <p className="text-gray-600">
                                        Don't have an account?{' '}
                                        <Link to="/employeesignup" className="text-purple-600 hover:text-indigo-600 font-semibold">
                                            Create Account
                                        </Link>
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="mt-8 grid grid-cols-3 gap-4 text-center">
                            <div>
                                <div className="text-purple-600 font-bold text-2xl">Secure</div>
                                <div className="text-gray-600 text-sm">Protected</div>
                            </div>
                            <div>
                                <div className="text-purple-600 font-bold text-2xl">Fast</div>
                                <div className="text-gray-600 text-sm">Access</div>
                            </div>
                            <div>
                                <div className="text-purple-600 font-bold text-2xl">24/7</div>
                                <div className="text-gray-600 text-sm">Support</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AdminUserLogin;